import React, { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getL1CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { track } from '@gumtree/shared/src/actions';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { getSaveAdPath, toggleSavedAdThunk } from '@gumtree/shared/src/reducers/saved-ads';
import type { ShellState, ShellUseDispatch } from '@gumtree/shell/src/reducers/common';
import { PageLayout } from '@gumtree/shared/src/types/srp';
import { useModal } from '@gumtree/shell/src/use-modal';
import { SearchAd } from '@gumtree/shared/src/model/search-ads-model';
import { SellerAd } from '@gumtree/shared/src/model/seller-other-ads-model';
import { getClickListingDetails } from '@gumtree/shared/src/util/listing-details';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { SellerType } from '@gumtree/shared/src/types/advertising/partnership';

import { Favourite, Icon } from '..';
import { removeTrailingZeros, setPriceToZeroIfFreebies } from '../utils/currency-service';

import { handleListingClick } from './listing-tile.utils';
import ListingTileThumbnail, { ImageProp } from './listing-tile-thumbnail';

import {
    A,
    Description,
    ImageBadge,
    ImageCounterLabelStyle,
    ImageWrapper,
    Spotlight,
    Wrapper,
} from './listing-tile.style';
import TileInfoMobileForSale from './tile-info-mobile-for-sale';
import TileInfoJobs from './tile-info-jobs';
import TileInfoGeneral from './tile-info-general';
import { qaAttribute } from '../utils/qa-service';
import { ContentWrapper } from './content-wrapper/content-wrapper';
import { CounterWrapper } from './counter-wrapper/counter-wrapper';
import { mapAttributesByCategory } from './attributes/attributes-by-category-mapper';

export interface Props {
    style?: React.CSSProperties;
    isLazyLoadImage?: boolean;
    isHighlighted?: boolean;
    children?: ReactNode;
    pageLayout: PageLayout;
    ad: SearchAd | SellerAd;
    type?: 'listing' | 'profile-listing';
    showPostDate?: boolean;
    setOpenHPModal?: (open: boolean) => void;
}

const ListingTile = ({
    isLazyLoadImage = true,
    pageLayout,
    isHighlighted,
    ad,
    type = 'listing',
    showPostDate,
    setOpenHPModal,
    ...otherProps
}: Props) => {
    const {
        id: adId,
        accountId,
        path,
        shortDescription,
        price,
        vat,
        imageUrl,
        title,
        l1CategoryId,
        l2CategoryId,
        location,
        postedDate: timeSincePosted,
        featured = false,
        urgent = false,
        attributes = [],
        numberOfImages = 0,
        hasVideo = false,
        sellerId,
        adSource = 'GT',
    } = ad as SearchAd | SellerAd;

    const sellerType =
        (ad.attributes?.find((attr) => attr.key === 'seller_type')?.value as SellerType) ?? null;

    const image: ImageProp = { src: imageUrl || '', alt: title };

    const { token, savedAds, userLoggedIn, buyerUrl } = useSelector(
        ({ userSavedAds, userData, baseConfig }: ShellState) => ({
            savedAds: userSavedAds.savedAds,
            token: userSavedAds.token,
            userLoggedIn: !!userData && userData.userLoggedIn,
            buyerUrl: baseConfig.buyerUrl,
            sellerUrl: baseConfig.sellerUrl,
        })
    );

    const { openDialog } = useModal();

    const favourited = Boolean(savedAds[adId]);
    const tileHasImages = l1CategoryId !== getL1CategoryIdBySeoName('jobs');
    const numberOfVideo = 1;
    const jobSalary = attributes.find((item) => item.key === 'salary_text')?.value ?? '';
    const supportShipping = attributes.find((item) => item.key === 'support_shipping')?.value === 'True';

    const dispatch = useDispatch() as ShellUseDispatch;

    const toggleFavourite = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!favourited) {
                track('WatchlistAddAttempt');
                trackPermutiveCustomEvent(window, PermutiveEventNames.WATCHLIST_ADD_ATTEMPT, {});
            }

            const trackEventPayload = {
                listName: type === 'profile-listing' ? 'Profile' : 'Search Result',
                clickListingDetails: getClickListingDetails(ad),
            };

            if (!userLoggedIn) {
                const redirectUrl = `${buyerUrl}${getSaveAdPath('add', adId, token)}`;
                openDialog(e, LoginModalType.FAVOURITE, true, redirectUrl);
                return;
            }

            dispatch(
                toggleSavedAdThunk({
                    advertId: adId,
                    token: token as string,
                    isAlreadySaved: favourited,
                    trackEventPayload,
                })
            );
        },
        [favourited]
    );

    const getAttributesOrDescription = () => {
        const renderedAttributes = mapAttributesByCategory({ attributes, l1CategoryId });

        return {
            isAttributes: !!renderedAttributes,
            render: () => renderedAttributes || <Description>{shortDescription}</Description>,
        };
    };

    const getCardInfo = () => {
        const attributesOrDescription = getAttributesOrDescription();
        const favouriteToggle =
            token && adSource === 'GT' ? (
                <Favourite onClick={toggleFavourite} isActive={favourited} />
            ) : undefined;
        const renderedPrice = removeTrailingZeros(setPriceToZeroIfFreebies(price, l2CategoryId));

        if (
            l1CategoryId === getL1CategoryIdBySeoName('for-sale') &&
            pageLayout === PageLayout.TwoColumns
        ) {
            return (
                <TileInfoMobileForSale
                    title={title}
                    favouriteToggle={favouriteToggle}
                    location={location}
                    price={renderedPrice}
                    vat={vat}
                    supportShipping={supportShipping}
                />
            );
        } else {
            if (l1CategoryId === getL1CategoryIdBySeoName('jobs')) {
                return (
                    <TileInfoJobs
                        isGridViewOnDesktop={pageLayout !== PageLayout.OneColumn}
                        title={title}
                        favouriteToggle={favouriteToggle}
                        attributesOrDescription={attributesOrDescription}
                        location={location}
                        timeSincePosted={timeSincePosted}
                        urgent={urgent}
                        featured={featured}
                        image={image}
                        salary={jobSalary}
                    />
                );
            } else {
                return (
                    <TileInfoGeneral
                        isGridViewOnDesktop={pageLayout !== PageLayout.OneColumn}
                        title={title}
                        favouriteToggle={favouriteToggle}
                        attributesOrDescription={attributesOrDescription}
                        location={location}
                        timeSincePosted={timeSincePosted}
                        price={renderedPrice || jobSalary}
                        vat={vat}
                        sellerId={sellerId}
                        l1CategoryId={l1CategoryId}
                        supportShipping={supportShipping}
                        l2CategoryId={l2CategoryId}
                        showDate={showPostDate}
                        sellerType={sellerType}
                        accountId={accountId}
                        setOpenHPModal={setOpenHPModal}
                    />
                );
            }
        }
    };

    const handleClick = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            await handleListingClick({
                e,
                type,
                ad,
                path,
            });
        },
        [type, ad, path]
    );

    return (
        <Wrapper {...otherProps} pageLayout={pageLayout} {...qaAttribute('search-result')}>
            <A
                {...qaAttribute('search-result-anchor')}
                href={path}
                onClick={handleClick}
                pageLayout={pageLayout}
            >
                {tileHasImages && (
                    <ImageWrapper pageLayout={pageLayout}>
                        <ListingTileThumbnail isLazy={isLazyLoadImage} image={image} />
                        {urgent && <ImageBadge isUrgent>Urgent</ImageBadge>}
                        {featured && <ImageBadge isFeatured>Featured</ImageBadge>}

                        <CounterWrapper pageLayout={pageLayout}>
                            <div
                                css={ImageCounterLabelStyle}
                                {...qaAttribute('tile-image-counter-label')}
                            >
                                <Icon type="camera" />
                                {numberOfImages}
                            </div>
                            {hasVideo && (
                                <div
                                    css={ImageCounterLabelStyle}
                                    {...qaAttribute('tile-video-counter-label')}
                                >
                                    <Icon type="video" />
                                    {numberOfVideo}
                                </div>
                            )}
                        </CounterWrapper>
                    </ImageWrapper>
                )}

                <ContentWrapper pageLayout={pageLayout}>{getCardInfo()}</ContentWrapper>
                {isHighlighted && <Spotlight data-q="listing-tile-spotlight">SPOTLIGHT</Spotlight>}
            </A>
        </Wrapper>
    );
};

export default ListingTile;
